import { invoke } from '@tauri-apps/api/core';

/**
 * Where an EMPTY personal folder lives.
 *
 * Folders were built as a derived thing: a folder exists exactly as long as an
 * item carries its name (`selectFolders` reads the plaintext index). That needs
 * no registry, no migration and no orphan handling — but it cannot express an
 * empty folder, so a "+ New folder" button would create something that vanishes
 * the moment the modal closes.
 *
 * This is the smallest thing that fixes that: a hidden vault item holding the
 * list. It follows the convention already in the codebase for exactly this kind
 * of internal state — `__e2e_keypair__` and `__e2e_seen_keys__` (CONTRACTS.md
 * §3) — so it syncs across devices through the existing machinery, is encrypted
 * like any other item, and `visibleItems` strips it from the UI by the `__`
 * name-prefix rule without any new filtering.
 *
 * The alternative, a real synced folder entity, is more "correct" and is a new
 * record type with new merge rules and orphan handling. Days from launch that is
 * a lot of new surface for something this carries adequately.
 *
 * THE LIST IS NOT THE TRUTH — it is a supplement. The folders a user sees are
 * the union of (names on items) and (names here). That ordering matters: if this
 * item is missing, unreadable, or lost to a merge, every folder that actually
 * holds something still appears. Only empty folders can be lost, and an empty
 * folder is by definition holding nothing.
 */

export const FOLDER_REGISTRY_ITEM_NAME = '__folders__';

/** Same normalisation `selectFolders` and `derive_index` apply. */
const clean = (v) => (typeof v === 'string' ? v.trim() : '');

/** De-duplicate case-insensitively, keeping first-seen casing, sorted. */
export function normaliseFolderNames(names) {
  const seen = new Map();
  for (const raw of Array.isArray(names) ? names : []) {
    const name = clean(raw);
    if (!name) continue;
    const key = name.toLowerCase();
    if (!seen.has(key)) seen.set(key, name);
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}

/**
 * Folders to show: the ones in use, plus the empty ones recorded here.
 *
 * `inUse` wins on casing, because an item actually carries that spelling.
 */
export function mergeFolderSources(inUse, registered) {
  // Array.isArray on BOTH, not `|| []`. Spreading a string spreads its
  // CHARACTERS, so a corrupt registry holding "nonsense" would have become
  // eight one-letter folders in the sidebar. A test caught it; the guard is the
  // fix rather than trusting the caller.
  const a = Array.isArray(inUse) ? inUse : [];
  const b = Array.isArray(registered) ? registered : [];
  return normaliseFolderNames([...a, ...b]);
}

/** The registry item from a `vault_list` result, or null. */
export function findRegistryItem(envelopes) {
  const list = Array.isArray(envelopes) ? envelopes : [];
  return list.find((it) => it?.name === FOLDER_REGISTRY_ITEM_NAME) || null;
}

/** Read the recorded names. Never throws — a broken registry must not break the vault. */
export async function loadRegisteredFolders() {
  try {
    const existing = findRegistryItem(await invoke('vault_list'));
    if (!existing) return [];
    const full = await invoke('vault_get_item', { id: existing.id });
    return normaliseFolderNames(full?.data?.folders);
  } catch {
    // A locked vault, a decrypt failure, a shape from a future build — none of
    // these are worth failing the sidebar for. Folders in use still show.
    return [];
  }
}

/**
 * Record a folder name so it survives having no items.
 *
 * Read-modify-write rather than blind overwrite: another device may have added
 * one since this client last looked, and clobbering it would delete their empty
 * folder. Returns the new list.
 */
export async function registerFolder(name) {
  const wanted = clean(name);
  if (!wanted) return loadRegisteredFolders();

  const existing = findRegistryItem(await invoke('vault_list'));
  const current = existing
    ? normaliseFolderNames((await invoke('vault_get_item', { id: existing.id }))?.data?.folders)
    : [];

  if (current.some((f) => f.toLowerCase() === wanted.toLowerCase())) return current;
  const next = normaliseFolderNames([...current, wanted]);

  await invoke('vault_put_item', {
    item: {
      ...(existing?.id ? { id: existing.id } : {}),
      type: 'custom',
      name: FOLDER_REGISTRY_ITEM_NAME,
      data: { folders: next },
    },
  });
  return next;
}
